import type { DirectionCard, PaletteRoleId, RoleToken } from "../domain.js";

const cssName = (value: string): string => value.replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase();
const cssComment = (value: string): string => value.replace(/\*\//g, "* /").replace(/[\r\n\u2028\u2029]+/g, " ").trim();
const cssColor = (value: string): string => {
  if (!/^#[0-9A-F]{6}$/i.test(value)) throw new Error(`Unsafe color token in CSS export: ${value}`);
  return value.toUpperCase();
};
const selectedPalette = (direction: DirectionCard) =>
  direction.colour?.systems.find((system) => system.id === direction.selected.paletteSystemId) ?? null;

export const exportCssCustomProperties = (direction: DirectionCard): string => {
  const palette = selectedPalette(direction);
  const lines = [
    `/* Color, Please — ${cssComment(direction.name)}`,
    `   Palette status: ${direction.truth.colourStatus}`,
    "   Recheck final pair contrast in the actual deck. */",
    ":root {",
  ];
  if (palette) {
    for (const [role, token] of Object.entries(palette.roles) as Array<[PaletteRoleId, RoleToken]>) {
      lines.push(`  --color-please-${cssName(role)}: ${cssColor(token.hex)};`);
    }
  }
  lines.push("}", "");
  return lines.join("\n");
};

export const exportPlainTextStyleSheet = (direction: DirectionCard): string => {
  const palette = selectedPalette(direction);
  const lines = [`COLOR, PLEASE — ${direction.name}`, ""];
  if (!palette) lines.push("No image was processed.");
  else {
    lines.push(`${palette.name} — ${palette.strategy.replaceAll("_", " ")}; ${palette.mode} slides`, "");
    for (const token of Object.values(palette.roles)) lines.push(`${token.role}: ${token.hex} — ${token.reason}`);
    if (palette.rationale.length) lines.push("", "WHY THIS WORLD", ...palette.rationale.map((line) => `- ${line}`));
    if (palette.warnings.length) lines.push("", "WATCH OUT FOR", ...palette.warnings.map((item) => `- ${item.message}`));
  }
  lines.push("", "HONEST LIMIT", "This palette was built locally from normalized source pixels. Recheck real copy, chart semantics, projection, and print before final handoff.", "");
  return lines.join("\n");
};

export const exportDesignTokens = (direction: DirectionCard): string => {
  const palette = selectedPalette(direction);
  return `${JSON.stringify({
    schemaVersion: "1.0.0",
    product: "Color, Please",
    projectId: direction.projectId,
    name: direction.name,
    paletteStatus: direction.truth.colourStatus,
    color: palette ? {
      systemId: palette.id,
      systemName: palette.name,
      strategy: palette.strategy,
      mode: palette.mode,
      roles: Object.fromEntries(Object.entries(palette.roles).map(([id, token]) => [id, { hex: token.hex, rgb: token.rgb, oklch: token.oklch, provenance: token.provenance }])),
    } : null,
  }, null, 2)}\n`;
};
